import React, { useEffect, useState, useMemo, useCallback } from "react";
import Layout from "../components/Layout.jsx";
import {
  Table,
  Spinner,
  Button,
  Form,
  Modal,
  Badge,
  Card,
  Alert,
} from "react-bootstrap";
import {
  employeeKpiViewService,
  projectDropdownService,
} from "../services/apiService.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { useNavigate } from "react-router-dom";
import FYWeekPicker from "../components/FYWeekPicker"; // [EDITED: KPI Task - Mahesh Sharma]
import { formatFYWeekLabel } from "../utils/fyWeekUtils"; // [EDITED: KPI Task - Mahesh Sharma]
import "./EmployeeKpiView.css";

/* ==============================
   Constants & Configuration
================================ */

const DATE_FORMAT_OPTIONS = {
  day: "2-digit",
  month: "short",
  year: "numeric",
};

const STATUS_CONFIG = {
  NOT_COMPLETED: {
    label: "Not Completed",
    variant: "danger",
    bg: "bg-danger",
    text: "text-white",
  },
  DRAFT: {
    label: "Draft",
    variant: "secondary",
    bg: "bg-light",
    text: "text-dark",
  },
  PENDING_TL: {
    label: "TL Review Pending",
    variant: "warning",
    bg: "bg-warning",
    text: "text-dark",
  },
  PENDING_HOD: {
    label: "HOD Review Pending",
    variant: "warning",
    bg: "bg-warning",
    text: "text-dark",
  },
  PENDING_ADMIN: {
    label: "Admin Review Pending",
    variant: "warning",
    bg: "bg-warning",
    text: "text-dark",
  },
  COMPLETED: {
    label: "Completed",
    variant: "success",
    bg: "bg-success",
    text: "text-white",
  },
};

/* ==============================
   Utility Functions
================================ */

const formatDate = (date) => {
  if (!date) return "-";

  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return "-";

    return dateObj.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "-";
  }
};

// Convert '2026-W08' to Date object for Monday of that week
function weekCodeToMonday(weekCode) {
  if (!weekCode) return null;
  const [year, week] = weekCode.split("-W");
  if (!year || !week) return null;
  // Week 1 is the week with the first Thursday of the year (ISO 8601)
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  const ISOweekStart = new Date(simple);
  if (dow <= 4) ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  else ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  return ISOweekStart;
}

const getWeekRange = (weekCode) => {
  const monday = weekCodeToMonday(weekCode);
  if (!monday) return "-";
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const formatDate = (d) =>
    d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  return `${formatDate(monday)} - ${formatDate(sunday)}`;
};

const STATUS_ORDER = [
  "Not Completed",
  "Draft",
  "TL Review Pending",
  "HOD Review Pending",
  "Admin Review Pending",
  "Completed"
];

const normalizeStatus = (status = "") => {
  const s = status.toLowerCase();

  if (s.includes("draft")) return "Draft";
  if (s.includes("not completed")) return "Not Completed";
  if (s.includes("tl")) return "TL Review Pending";
  if (s.includes("hod")) return "HOD Review Pending";
  if (s.includes("admin")) return "Admin Review Pending";
  if (s.includes("complete") || s.includes("approved"))
    return "Completed";

  return "Draft";
};

// Function to get status dot color
const getStatusDotColor = (status) => {
  if (!status) return "#6c757d";
  const s = status.toLowerCase();
  if (s.includes("draft")) return "#6c757d"; // Grey for draft
  if (
    s.includes("pending") ||
    s.includes("review pending") ||
    s.includes("tl review pending") ||
    s.includes("hod review pending") ||
    s.includes("admin review pending")
  ) {
    return "#ffc107"; // Yellow for pending
  }
  if (s.includes("approved") || s.includes("completed")) {
    return "#198754"; // Green for completed
  }
  if (s.includes("rejected")) return "#dc3545"; // Red for rejected
  return "#6c757d"; // Default grey
};

/**
 * ✅ MAIN STATUS ENGINE
 */
const getConsolidatedStatus = (entries, userRole) => {
  if (!entries || entries.length === 0) return "-";

  // ---- find LOWEST status from API ----
  let lowestIndex = STATUS_ORDER.length - 1;

  entries.forEach(e => {
    const normalized = normalizeStatus(e.status);
    const index = STATUS_ORDER.indexOf(normalized);

    if (index !== -1 && index < lowestIndex) {
      lowestIndex = index;
    }
  });

  let finalStatus = STATUS_ORDER[lowestIndex];

  const role = (userRole || "").toLowerCase();

  const isTL =
    role.includes("team lead") || role.includes("tl");

  const isHOD =
    role.includes("hod") || role.includes("head of department");

  // ===============================
  // ROLE BASED STATUS VIEW
  // ===============================

  // ✅ Team Lead should NOT see TL Pending
  if (isTL && finalStatus === "TL Review Pending") {
    finalStatus = "HOD Review Pending";
  }

  // ✅ HOD should NOT see TL/HOD pending
  if (isHOD) {
    if (
      finalStatus === "TL Review Pending" ||
      finalStatus === "HOD Review Pending"
    ) {
      finalStatus = "Admin Review Pending";
    }
  }

  return finalStatus;
};

/* ==============================
   Sub-Components
================================ */

const WeekSelectionModal = ({ show, onHide, onContinue }) => {
  const [selectedWeek, setSelectedWeek] = useState("");
  const [error, setError] = useState("");

  // Get current and previous week in YYYY-Www format
  const getCurrentAndPrevWeek = () => {
    const now = new Date();
    const year = now.getFullYear();
    // Get week number
    const firstJan = new Date(year, 0, 1);
    const days = Math.floor((now - firstJan) / (24 * 60 * 60 * 1000));
    const week = Math.ceil((days + firstJan.getDay() + 1) / 7);
    const currWeek = `${year}-W${week.toString().padStart(2, "0")}`;
    let prevWeek;
    if (week === 1) {
      // Previous week is last week of previous year
      const prevYear = year - 1;
      const lastDayPrevYear = new Date(prevYear, 11, 31);
      const daysPrev = Math.floor(
        (lastDayPrevYear - new Date(prevYear, 0, 1)) / (24 * 60 * 60 * 1000),
      );
      const lastWeekPrevYear = Math.ceil(
        (daysPrev + new Date(prevYear, 0, 1).getDay() + 1) / 7,
      );
      prevWeek = `${prevYear}-W${lastWeekPrevYear.toString().padStart(2, "0")}`;
    } else {
      prevWeek = `${year}-W${(week - 1).toString().padStart(2, "0")}`;
    }
    return { currWeek, prevWeek };
  };

  const { currWeek: maxWeek } = getCurrentAndPrevWeek();

  const handleContinue = () => {
    if (!selectedWeek) return;
    if (selectedWeek > maxWeek) {
      setError("You cannot select a future week.");
      return;
    }
    setError("");
    onContinue(selectedWeek);
    setSelectedWeek("");
  };

  const handleClose = () => {
    setSelectedWeek("");
    setError("");
    onHide();
  };

  // Direct click handler for the input
  const handleInputClick = (e) => {
    e.stopPropagation();
    if (e.target.showPicker) {
      e.target.showPicker();
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="h4">
          <i
            className="bi bi-calendar-week me-2"
            style={{ color: "#76df23" }}
          ></i>
          Select Week
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-3">
        <p className="text-muted mb-3">
          Please select the week for your KPI submission:
        </p>
        <Form.Group>
          <FYWeekPicker
            value={selectedWeek}
            onChange={(val) => {
              setSelectedWeek(val);
              setError("");
            }}
            className="w-100"
            style={{ width: "100%" }}
          />
          {error && <div className="text-danger mt-2 small">{error}</div>}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          className="px-4"
        >
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleContinue}
          disabled={!selectedWeek}
          className="px-4"
          style={{ backgroundColor: "#76df23", borderColor: "#76df23" }}
        >
          Continue <i className="bi bi-arrow-right ms-2"></i>
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const FilterSection = ({ pendingWeek, onWeekChange, onClear, isFiltered, onAddKpiClick }) => {
  return (
    <Card className="border-0 shadow-sm mb-2">
      <Card.Body className="p-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <Form.Group className="d-flex align-items-center mb-0" style={{ minWidth: "260px" }}>
              <Form.Label className="fw-semibold text-secondary small mb-0 me-2" style={{ whiteSpace: "nowrap" }}>
                Filter by Week:
              </Form.Label>
              <FYWeekPicker
                size="sm"
                value={pendingWeek}
                onChange={onWeekChange}
                style={{ minWidth: "280px" }}
              />
              {isFiltered && (
                <Button
                  variant="outline-secondary"
                  onClick={onClear}
                  size="sm"
                  className="ms-2"
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              )}
            </Form.Group>
          </div>
          <Button
            variant="success"
            onClick={onAddKpiClick}
            size="sm"
            className="px-3 shadow-sm"
            style={{
              backgroundColor: "#76df23",
              borderColor: "#76df23",
            }}
          >
            <i className="bi bi-plus-circle me-1"></i>
            Add KPI
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

const EmptyState = ({ colSpan }) => (
  <tr>
    <td colSpan={colSpan} className="text-center py-5">
      <div className="text-muted">
        <i
          className="bi bi-clipboard-data fs-1 d-block mb-3"
          style={{ opacity: 0.5 }}
        ></i>
        <h6 className="fw-normal">No KPI data found</h6>
        <p className="small mb-0">
          Start by adding your first KPI using the "Add KPI" button
        </p>
      </div>
    </td>
  </tr>
);

/* ==============================
   Main Component
================================ */

const EmployeeKpiView = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // State management
  const [groupedKpis, setGroupedKpis] = useState([]); // [{ week, employee_name, submission_date, target_date, status, count, first_kpi_id }]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWeekModal, setShowWeekModal] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    week: "",
  });

  // Pending filter states
  const [pendingFilters, setPendingFilters] = useState({
    week: "",
  });

  /* ==============================
     User Role Check
  ================================= */

  const userRoles = useMemo(() => {
    const roleStr = user?.role?.toLowerCase() || "";
    return {
      isTeamLead: ["team lead", "tl"].some((role) => roleStr.includes(role)),
      isHod: ["hod", "head of department"].some((role) =>
        roleStr.includes(role),
      ),
      isEmployee: !["team lead", "tl", "hod", "head of department"].some(
        (role) => roleStr.includes(role),
      ),
    };
  }, [user]);

  /* ==============================
     Data Fetching
  ================================= */

  // Fetch grouped KPIs by week
  const fetchGroupedKpis = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await employeeKpiViewService.getGroupedKpis();
      // API returns { success, message, data: [{ week, week_start, entries: [...] }] }
      const data = response?.data || response || [];
      setGroupedKpis(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(
        "Unable to load grouped KPI data. Please refresh the page or try again later.",
      );
      setGroupedKpis([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroupedKpis();
  }, [fetchGroupedKpis]);

  /* ==============================
     Computed Properties
  ================================= */

  const [projectNames, setProjectNames] = useState([]);

  useEffect(() => {
    projectDropdownService
      .getProjects()
      .then((projects) => {
        setProjectNames(
          Array.isArray(projects)
            ? projects.map((p) => p.project_name).filter(Boolean)
            : [],
        );
      })
      .catch(() => setProjectNames([]));
  }, []);

  const isFiltered = useMemo(() => {
    return Boolean(filters.week);
  }, [filters]);

  /* ==============================
     Filtering Logic
  ================================= */

  // Filtering for groupedKpis by week
  const filteredGroupedKpis = useMemo(() => {
    let result = [...groupedKpis];
    if (filters.week) {
      result = result.filter((row) => row.week === filters.week);
    }
    return result;
  }, [groupedKpis, filters]);

  /* ==============================
     Event Handlers
  ================================= */

  const handleApplyFilters = useCallback(() => {
    setFilters({ ...pendingFilters });
  }, [pendingFilters]);

  const handleClearFilters = useCallback(() => {
    setFilters({ week: "" });
    setPendingFilters({ week: "" });
  }, []);

  const handleWeekChange = useCallback((value) => {
    setPendingFilters((prev) => ({ ...prev, week: value }));
    setFilters((prev) => ({ ...prev, week: value }));
  }, []);

  const handleAddKpiClick = useCallback(() => {
    setShowWeekModal(true);
  }, []);

  const handleWeekModalContinue = useCallback(
    (selectedWeek) => {
      setShowWeekModal(false);
      // Redirect to /employee-kpi-view with week as query param
      navigate(`/employee-kpi-view?week=${selectedWeek}`);
    },
    [navigate],
  );

  const handleWeekModalClose = useCallback(() => {
    setShowWeekModal(false);
  }, []);

  // On week row click, go to KPI submission for that week (pass week param)
  const handleWeekRowClick = useCallback(
    (week) => {
      if (week) {
        navigate(`/employee-kpi-submission/${encodeURIComponent(week)}`);
      }
    },
    [navigate],
  );

  /* ==============================
     Table Column Configuration
  ================================= */

  const columnConfig = useMemo(() => {
    const columns = [
      { key: "week", label: "Week", width: "180px" },
      { key: "submission", label: "Submission Date", width: "130px" },
    ];

    if (!userRoles.isTeamLead && !userRoles.isHod) {
      columns.push({ key: "tlRating", label: "TL Rating", width: "100px" });
    }

    if (!userRoles.isHod) {
      columns.push({ key: "hodRating", label: "HOD Rating", width: "100px" });
    }

    columns.push({ key: "adminRating", label: "Admin Rating", width: "100px" });
    columns.push({ key: "status", label: "Status", width: "180px" });

    return columns;
  }, [userRoles]);

  /* ==============================
   Render
================================ */

  return (
    <Layout>
      <main id="main" className="main" style={{ padding: 0 }}>
        <div className="container-fluid" style={{ padding: 0 }}>
          {/* Header Section */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <h1
                className="mb-1 fw-bold"
                style={{ color: "#76df23", fontSize: "24px" }}
              >
                MY KPI
              </h1>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert
              variant="danger"
              className="mb-4 border-0 shadow-sm"
              dismissible
              onClose={() => setError(null)}
            >
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error}
            </Alert>
          )}

          {/* Week Selection Modal - Show for all roles when Add KPI is clicked */}
          <WeekSelectionModal
            show={showWeekModal}
            onHide={handleWeekModalClose}
            onContinue={handleWeekModalContinue}
          />

          {/* Filter Section */}
          <FilterSection
            pendingWeek={pendingFilters.week}
            onWeekChange={handleWeekChange}
            onClear={handleClearFilters}
            isFiltered={isFiltered}
            onAddKpiClick={handleAddKpiClick}
          />

          {/* KPI Table Card */}
          <Card className="border-0 shadow-sm ">
            <Card.Header className="bg-white border-0 pt-4 pb-0 px-4">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  {userRoles.isTeamLead ? (
                    <h5 className="mb-0 fw-semibold">Team KPI Submissions</h5>
                  ) : (
                    <>
                      <h5 className="mb-0 fw-semibold">
                        {userRoles.isHod
                          ? "Department KPI Submissions"
                          : "My KPI Submissions"}
                      </h5>
                    </>
                  )}
                </div>
                <span className="text-muted small">
                  <i className="bi bi-info-circle me-1"></i>
                  {filteredGroupedKpis.length}{" "}
                  {filteredGroupedKpis.length === 1 ? "entry" : "entries"} found
                </span>
              </div>
            </Card.Header>

            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner
                    animation="border"
                    style={{ color: "#76df23", width: "3rem", height: "3rem" }}
                  />
                  <p className="text-muted mt-3 mb-0">Loading KPIs...</p>
                </div>
              ) : (
                <div className="table-responsive-scroll" style={{ fontSize: "12px", maxHeight: "calc(100vh - 400px)", overflowY: "auto", minHeight:"350px" }}>
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-4">Week</th>
                        <th>Submission Date</th>
                        <th>Status</th>
                        <th>Entries</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredGroupedKpis.length === 0 ? (
                        <tr>
                          <td
                            colSpan={4}
                            className="text-center text-muted py-4"
                          >
                            No KPI records found
                          </td>
                        </tr>
                      ) : (
                        filteredGroupedKpis.map((row, idx) => {
                          const entries = row.entries || [];
                          const firstEntry = entries[0] || {};

                          // Get the latest submission date from all entries
                          const submissionDates = entries
                            .map((e) => e.submission_date)
                            .filter((date) => date); // Remove null/undefined

                          // Get the most recent submission date (assuming they're in format like "16-Feb-26")
                          // Since dates are in string format, we'll just take the first one that exists
                          const latestSubmissionDate =
                            submissionDates.length > 0
                              ? submissionDates[0]
                              : "-";

                          // Determine consolidated status based on rating hierarchy
                          const status = getConsolidatedStatus(
                            entries,
                            user?.role,
                          );

                          const count = entries.length;

                          return (
                            <tr
                              key={row.week}
                              style={{ cursor: "pointer" }}
                              onClick={() => handleWeekRowClick(row.week)}
                            >
                              <td className="ps-4 fw-medium">
                                <span className="text-dark fw-bold">{formatFYWeekLabel(row.week)}</span> {/* [EDITED: KPI Task - Mahesh Sharma] */}
                                <br />
                                <span className="text-muted small">
                                  {row.week} • {getWeekRange(row.week)}
                                </span>
                              </td>
                              <td>{latestSubmissionDate}</td>
                              <td>
                                <div className="d-flex align-items-center justify-content-center">
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: "10px",
                                      height: "10px",
                                      borderRadius: "50%",
                                      backgroundColor: getStatusDotColor(status),
                                      marginRight: "8px"
                                    }}
                                  ></span>
                                  <span style={{ fontSize: "0.875rem" }}>
                                    {status}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <span className="badge bg-info text-dark px-3 py-2">
                                  {count}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </main>
    </Layout>
  );
};

export default EmployeeKpiView;